export const PROJECT_CATEGORIES = [
    {
        id: 'banking',
        label: 'Banking',
        headline: 'Banking & Financial Services',
        overview:
            'Solution design and architecture for tier-1 Australian banks, covering cloud migration, fraud remediation, and core platform modernization in regulated environments.',
        focusAreas: ['Digital banking', 'Fraud & cyber crime', 'Cloud migration', 'Salesforce / Vlocity'],
        projects: [
            {
                id: 'bank-cloud-migration',
                title: 'Cloud Migration and Infrastructure Design',
                summary:
                    'Defined the cloud migration strategy for a major Australian bank moving from dedicated data centers to Google Cloud Platform.',
                businessContext:
                    'The bank required a compliant path off legacy data centers while maintaining uptime for critical payment and lending systems.',
                industry: 'Banking & Financial Services',
                role: 'Lead Solution Architect',
                impact:
                    'Established landing zones, security controls, and an infrastructure-as-code foundation for phased workload migration.',
                technologies: ['Banking', 'Infra Design', 'Security Architecture', 'Infrastructure as Code'],
            },
            {
                id: 'bank-fraud',
                title: 'Fraud and Cyber Crime - Solution Consultant',
                summary:
                    'Designed detection and remediation solutions for fraudulent activity across multiple banking channels.',
                businessContext:
                    'Rising digital channel fraud required real-time monitoring, case management integration, and audit-ready traceability.',
                industry: 'Banking & Financial Services',
                role: 'Solution Consultant',
                impact:
                    'Aligned fraud workflows with enterprise security standards and reduced manual investigation handoffs.',
                technologies: ['Banking', 'Fraud Detection', 'Cyber Security'],
            },
            {
                id: 'bank-digital-modernization',
                title: 'Digital Banking Platform Modernization',
                summary:
                    'Modernized core banking capabilities at NAB and ANZ through API-led connectivity and cloud-native design.',
                businessContext:
                    'Legacy core systems limited product velocity; the program targeted faster customer onboarding and omnichannel experiences.',
                industry: 'Banking & Financial Services',
                role: 'Solution Designer',
                impact:
                    'Enabled API-first integration with Salesforce and Vlocity for quoting, onboarding, and servicing journeys.',
                technologies: ['Banking', 'Salesforce', 'Vlocity', 'Cloud-Native', 'APIs'],
            },
        ],
    },
    {
        id: 'cloud-integration',
        label: 'Cloud Integration',
        headline: 'Cloud Integration & SaaS Connectivity',
        overview:
            'Enterprise integration patterns on AWS and Mulesoft, connecting SaaS platforms and legacy systems for real-time data exchange.',
        focusAreas: ['AWS serverless', 'Event-driven integration', 'SaaS hubs', 'Multi-system sync'],
        projects: [
            {
                id: 'cloud-native-platform',
                title: 'Cloud-Native Integration Platform',
                summary:
                    'Delivered a cloud-native integration platform on AWS for a major telecom provider.',
                businessContext:
                    'Disparate billing, CRM, and network systems needed near real-time synchronization without batch lag.',
                industry: 'Telecommunications',
                role: 'Integration Architect',
                impact:
                    'Synchronized 20+ enterprise systems using API Gateway, Lambda, and EventBridge with reduced integration latency.',
                technologies: ['AWS', 'API Gateway', 'Lambda', 'EventBridge', 'Microservices'],
            },
            {
                id: 'saas-hub',
                title: 'SaaS Integration Hub',
                summary:
                    'Architected a centralized hub connecting Salesforce, ServiceNow, SAP, and custom applications.',
                businessContext:
                    'Point-to-point integrations were slowing delivery; a hub model was needed for reuse and governance.',
                industry: 'Cross-industry Enterprise',
                role: 'Principal Integration Architect',
                impact: 'Reduced integration delivery time by 60% through standardized Mulesoft Anypoint patterns.',
                technologies: ['Mulesoft', 'Salesforce', 'ServiceNow', 'SAP', 'REST APIs'],
            },
        ],
    },
    {
        id: 'api-microservices',
        label: 'API & Microservices',
        headline: 'API Platforms & Microservices',
        overview:
            'API-first programs and microservices transformations for telecom and enterprise clients, emphasizing scalability, security, and developer experience.',
        focusAreas: ['Apigee API management', 'Microservices decomposition', 'Event-driven design', 'CI/CD'],
        projects: [
            {
                id: 'monolith-microservices',
                title: 'Monolith to Microservices Transformation',
                summary:
                    'Re-architected legacy monoliths into microservices interfacing with mainframe systems.',
                businessContext:
                    'Monolithic release cycles blocked feature delivery and inflated operational cost for telecom ordering systems.',
                industry: 'Telecommunications',
                role: 'Technical Lead / Solution Designer',
                impact:
                    'Cut operational expenses by 50% and introduced a microservices CI/CD pipeline with improved release frequency.',
                technologies: ['Microservices', 'Docker', 'Kubernetes', 'CI/CD', 'Elastic Stack'],
            },
            {
                id: 'api-first-platform',
                title: 'API-First Digital Platform',
                summary:
                    'Implemented an Apigee-based API platform for a telecom digital transformation program.',
                businessContext:
                    'Mobile and web channels required consistent, secured APIs with centralized lifecycle management.',
                industry: 'Telecommunications',
                role: 'API Platform Architect',
                impact:
                    'Managed 100+ APIs with OAuth policies, developer portal, and standardized OpenAPI specifications.',
                technologies: ['Apigee', 'API Design', 'OAuth 2.0', 'OpenAPI', 'Developer Portal'],
            },
            {
                id: 'event-order-mgmt',
                title: 'Event-Driven Order Management',
                summary:
                    'Built an event-driven order management system using Kafka and Spring Boot microservices.',
                businessContext:
                    'Order fulfillment needed guaranteed delivery and fault tolerance under peak telecom demand.',
                industry: 'Telecommunications',
                role: 'Solution Designer',
                impact:
                    'Enabled real-time order processing with resilient event sourcing and compensating transaction patterns.',
                technologies: ['Kafka', 'Spring Boot', 'Event Sourcing', 'CQRS', 'Docker'],
            },
        ],
    },
    {
        id: 'data-analytics',
        label: 'Data & Analytics',
        headline: 'Data Pipelines & Operational Analytics',
        overview:
            'Real-time data processing and observability platforms supporting high-volume telecom operations and executive decision-making.',
        focusAreas: ['Stream processing', 'Elasticsearch analytics', 'Microservices telemetry', 'Operational dashboards'],
        projects: [
            {
                id: 'java-microservices-platform',
                title: 'Java Microservices Platform',
                summary:
                    'Built a scalable Java microservices platform for a distributed telecom ordering system.',
                businessContext:
                    'Peak ordering events required elastic scaling, circuit breaking, and centralized configuration.',
                industry: 'Telecommunications',
                role: 'Technical Architect',
                impact:
                    'Improved system resilience with service discovery, circuit breakers, and containerized deployment.',
                technologies: ['Java', 'Spring Boot', 'Spring Cloud', 'Docker', 'Kubernetes'],
            },
            {
                id: 'realtime-pipeline',
                title: 'Real-Time Data Pipeline',
                summary:
                    'Designed a pipeline processing millions of daily telecom events for near real-time analytics.',
                businessContext:
                    'Operations teams needed sub-minute visibility into network and customer event streams.',
                industry: 'Telecommunications',
                role: 'Solution Designer',
                impact:
                    'Integrated Kafka Streams with Elasticsearch and Kibana for actionable operational dashboards.',
                technologies: ['Java', 'Kafka Streams', 'Elasticsearch', 'Kibana', 'Microservices'],
            },
        ],
    },
    {
        id: 'telecom',
        label: 'Telecom',
        headline: 'Telecommunications & Industry Cloud',
        overview:
            'OSS/BSS integration, Salesforce ecosystems, and Vlocity industry cloud solutions for Australian telecom enterprises.',
        focusAreas: ['Salesforce integration', 'Vlocity CPQ', 'SaaS orchestration', 'Product catalog'],
        projects: [
            {
                id: 'telecom-integration',
                title: 'Integration Designer - Telecom',
                summary:
                    'Integrated Salesforce and SaaS tools including Seismic, OpenAir, and SharePoint for a telecom enterprise.',
                businessContext:
                    'Sales and delivery teams relied on fragmented SaaS tools without consistent data flows.',
                industry: 'Telecommunications',
                role: 'Integration Designer',
                impact:
                    'Unified communication across platforms using API gateways, event streams, and messaging middleware.',
                technologies: ['Telecom', 'Salesforce', 'API Gateways', 'Event Streams'],
            },
            {
                id: 'vlocity-cpq',
                title: 'Vlocity CPQ & Industry Cloud',
                summary:
                    'Implemented Vlocity CPQ and Industry Cloud for telecom product catalog and order orchestration.',
                businessContext:
                    'Complex product bundles and channel-specific pricing required industry-specific CPQ capabilities.',
                industry: 'Telecommunications',
                role: 'Salesforce / Vlocity Architect',
                impact:
                    'Streamlined quoting and order orchestration across enterprise sales and partner channels.',
                technologies: ['Telecom', 'Vlocity', 'Salesforce', 'CPQ', 'Order Management'],
            },
        ],
    },
    {
        id: 'solution-design',
        label: 'Solution Design',
        headline: 'Enterprise Solution Design & Strategy',
        overview:
            'Target architecture, transformation roadmaps, and cloud strategy for large-scale programs spanning 50+ applications.',
        focusAreas: ['Enterprise architecture', 'TOGAF-aligned design', 'Migration roadmaps', 'Multi-cloud strategy'],
        projects: [
            {
                id: 'enterprise-digital-transform',
                title: 'Enterprise Digital Transformation',
                summary:
                    'Led solution design for a telecom digital transformation spanning ordering, billing, and network domains.',
                businessContext:
                    'A major Australian telecom needed a cohesive target state across 50+ interdependent applications.',
                industry: 'Telecommunications',
                role: 'Lead Solution Designer',
                impact:
                    'Defined integration patterns, migration waves, and architecture guardrails aligned to business outcomes.',
                technologies: ['Enterprise Architecture', 'TOGAF', 'Integration Patterns', 'Digital Transformation'],
            },
            {
                id: 'cloud-migration-strategy',
                title: 'Cloud Migration Strategy',
                summary:
                    'Architected multi-cloud migration from on-premise data centers to AWS and GCP for regulated workloads.',
                businessContext:
                    'Regulated industries required landing zones, network segmentation, and security frameworks before migration.',
                industry: 'Cross-industry Enterprise',
                role: 'Cloud Solution Architect',
                impact:
                    'Delivered landing zone blueprints and security architecture for compliant cloud adoption.',
                technologies: ['Cloud Strategy', 'AWS', 'GCP', 'Security Architecture', 'Landing Zones'],
            },
        ],
    },
];
